import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Center,
  HStack,
  IconButton,
  Image,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  BarChart3,
  CalendarClock,
  Clock,
  FileText,
  Gift,
  Image as ImageIcon,
  Keyboard,
  Mic,
  Check,
  Pencil,
  Reply,
  Send,
  Smile,
  Trash2,
  Video,
  X,
} from 'lucide-react';
import {
  EVERYONE_MENTION,
  MESSAGE_LIMITS,
  extractMentionHandles,
} from '@twinden/shared';
import { editSealed, isE2EEReady } from '../../lib/e2ee/send';
import { api, ApiError } from '../../lib/api';
import {
  prepareImage,
  sendAttachment,
  type AttachmentRequest,
} from '../../lib/attachments';
import { isRetryable, queueChange, queueMessage } from '../../lib/outbox';
import { sendAlbums, sendFile } from '../../lib/sendFiles';
import { shareKind } from '../../lib/shares';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import {
  currentUserAtom,
  photoQualityAtom,
  returnedCaptionAtom,
  shareDraftAtom,
} from '../../store';
import {
  renderCard,
  type CardChoice,
  type FestivalDayId,
  type OccasionId,
} from '../../lib/greetings';
import { GreetingSheet } from './GreetingSheet';
import { PollSheet } from './PollSheet';
import { GatheringSheet } from './GatheringSheet';
import { SendLaterSheet } from './SendLaterSheet';
import { ScheduledList } from './ScheduledList';
import { useScheduled } from '../../hooks/useScheduled';
import {
  KeysNotReadyError,
  resealOnce,
  scheduleErrorText,
  scheduleText,
  scheduledLabel,
  type ScheduledView,
} from '../../lib/scheduled';
import { computePeaks } from '../../lib/voicePeaks';
import {
  useVoiceRecorder,
  useWebSocket,
  type SentMessage,
  type VoiceRecording,
} from '../../hooks';
import { Waveform } from './Waveform';
import { LazyEmojiPicker } from './LazyEmojiPicker';
import { insertAtCaret } from '../../lib/emoji';
import {
  keyboardHeight,
  KEYBOARD_MIN_PX,
  lastKeyboardHeight,
} from '../../lib/viewport';
import { useDesktopPicker } from './useDesktopPicker';
import { ComposerPlusMenu } from './ComposerPlusMenu';
import {
  filterMentionCandidates,
  insertMention,
  mentionQueryAt,
  MentionPicker,
  type MentionCandidate,
} from './MentionPicker';
import { formatTime, replyLabel, type ChatMessage } from './messageUtils';
import {
  draftsAtom,
  loadDrafts,
  openDrafts,
  resolveDraftReply,
  saveDraft,
} from '../../lib/drafts';
import { t as tNow, useT } from '../../i18n';
import { shortenLinks } from '../../lib/shortLinks';

// The textarea grows with its content up to roughly five lines, then scrolls.
const MAX_INPUT_HEIGHT_PX = 128;
// A draft is saved this long after typing stops (and on leaving, D228).
const DRAFT_SAVE_DELAY_MS = 300;

function errorMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : tNow('common.somethingWrong');
}

// The emoji panel on phones takes the keyboard's place (D263): the
// keyboard's height (now, or the last time it was up on this device), else a
// comfortable default.
const EMOJI_PANEL_DEFAULT_PX = 300;
function emojiPanelHeightNow(): number {
  const measured = keyboardHeight();
  const height =
    measured >= KEYBOARD_MIN_PX
      ? measured
      : (lastKeyboardHeight() ?? EMOJI_PANEL_DEFAULT_PX);
  return Math.round(Math.min(420, Math.max(240, height)));
}

// Touch keyboards have no Shift+Enter, so Enter inserts a newline there and the
// send button sends. With a physical keyboard Enter sends.
function enterSends(): boolean {
  return !window.matchMedia?.('(pointer: coarse)').matches;
}

// Replying to or editing a message (M20, D20/D22).
export type ComposerMode =
  | { kind: 'reply'; message: ChatMessage }
  | { kind: 'edit'; message: ChatMessage };

const MAX_PICKED_FILES = 10;

interface MessageComposerProps {
  conversationId: string;
  mode: ComposerMode | null;
  onCancelMode: () => void;
  onSent: (message: SentMessage) => void;
  onEdited: (message: ChatMessage) => void;
  onFocus: () => void;
  /** Everyone else in the chat, for the @mention picker (D27). */
  mentionCandidates: MentionCandidate[];
  /** e.g. "Say something to everyone" (Living Room). */
  placeholder: string;
  /** Who the chat is with, in a direct chat: greeting cards name them (D144). */
  recipientName?: string;
  /** Open the greeting sheet on this card ("Wish them", D144). */
  greetOccasion?: OccasionId | null;
  /** …with this festival day's cards leading (Bhai Dooj, D315). */
  greetDay?: FestivalDayId | null;
  onGreetOpened?: () => void;
  /** The chat's family, kept with its draft (D228). */
  familyId?: string;
  /** The chat's loaded messages, to find a draft's reply target. */
  loadedMessages?: ChatMessage[];
  /** A draft's reply target, checked and still there: show the bar. */
  onRestoreReply?: (message: ChatMessage) => void;
}

export function MessageComposer({
  conversationId,
  mode,
  onCancelMode,
  onSent,
  onEdited,
  onFocus,
  mentionCandidates,
  placeholder,
  recipientName,
  greetOccasion,
  greetDay,
  onGreetOpened,
  familyId,
  loadedMessages,
  onRestoreReply,
}: MessageComposerProps) {
  const currentUser = useAtomValue(currentUserAtom);
  // The chat's draft from last time (M62, D228); the composer is keyed by
  // chat, so this is read once per chat.
  const [initialDraft] = useState(() =>
    currentUser ? loadDrafts(currentUser.id)[conversationId] : undefined
  );
  const [messageInput, setMessageInput] = useState(initialDraft?.text ?? '');
  // Drafts are opened from the device's sealed copy once per visit (D281);
  // a chat opened before that finishes picks its draft up when it's ready,
  // unless something has been typed meanwhile.
  const typedRef = useRef(false);
  useEffect(() => {
    if (!currentUser || initialDraft) return;
    let cancelled = false;
    void openDrafts(currentUser.id).then((drafts) => {
      const draft = drafts[conversationId];
      if (cancelled || !draft || typedRef.current) return;
      setMessageInput(draft.text);
      if (draft.replyToId) pendingReplyIdRef.current = draft.replyToId;
    });
    return () => {
      cancelled = true;
    };
    // Once per chat: the composer is keyed by chat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // A taller box rounds less, so the pill doesn't pinch a few lines of text.
  const multiline = messageInput.includes('\n') || messageInput.length > 60;
  // @mention picker (D27): the query being typed and the highlighted row.
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Emoji picker (M69, D263): a popover on desktop, a panel in the
  // keyboard's place on phones.
  const desktop = useDesktopPicker();
  // Phones: the panel's search box raised the keyboard, so the panel shrinks
  // to fit above it (D263).
  const [emojiSearching, setEmojiSearching] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiPanelHeight, setEmojiPanelHeight] = useState(
    EMOJI_PANEL_DEFAULT_PX
  );
  // Phones: a pick closes the panel only if the keyboard was down (D263).
  const closeEmojiAfterPick = useRef(false);
  const emojiPopoverRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const t = useT();
  const photoQuality = useAtomValue(photoQualityAtom);
  const { sendTyping } = useWebSocket();
  const waveColor = useColorModeValue('red.400', 'red.300');
  // Greeting cards (D144).
  const [greeting, setGreeting] = useState<{
    open: boolean;
    occasion: OccasionId | null;
    day?: FestivalDayId | null;
  }>({ open: false, occasion: null });
  useEffect(() => {
    if (!greetOccasion) return;
    setGreeting({ open: true, occasion: greetOccasion, day: greetDay });
    onGreetOpened?.();
  }, [greetOccasion, greetDay, onGreetOpened]);

  // Photos and videos picked together, waiting to go as an album (M60,
  // D219). The message box's text becomes its caption.
  const [albumDraft, setAlbumDraft] = useState<
    { file: File; url: string | null }[]
  >([]);
  const dropDraft = (keep: (index: number) => boolean = () => false) =>
    setAlbumDraft((draft) => {
      draft.forEach((item, index) => {
        if (!keep(index) && item.url) URL.revokeObjectURL(item.url);
      });
      return draft.filter((_, index) => keep(index));
    });
  useEffect(() => () => dropDraft(), [conversationId]);

  const [pollOpen, setPollOpen] = useState(false);
  const [gatheringOpen, setGatheringOpen] = useState(false);

  // Send later (M64, D239): this chat's scheduled messages, mine only.
  const scheduled = useScheduled(conversationId);
  const [sendLaterOpen, setSendLaterOpen] = useState(false);

  const editing = mode?.kind === 'edit' ? mode.message : null;
  const replyingTo = mode?.kind === 'reply' ? mode.message : null;

  // A draft belongs to the chat it was typed in. A link or text shared to
  // this chat starts it (D180).
  const [shareDraft, setShareDraft] = useAtom(shareDraftAtom);
  useEffect(() => {
    if (shareDraft?.conversationId === conversationId) {
      setMessageInput(shareDraft.text);
      setShareDraft(null);
      textareaRef.current?.focus();
      return;
    }
    // Only when the chat changes; the shared draft is read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // A lone photo's caption comes back if the photo is cancelled (D219).
  const [returnedCaption, setReturnedCaption] = useAtom(returnedCaptionAtom);
  useEffect(() => {
    if (returnedCaption?.conversationId !== conversationId) return;
    const text = returnedCaption.text;
    setMessageInput((current) =>
      current.trim() ? `${current} ${text}` : text
    );
    setReturnedCaption(null);
  }, [returnedCaption, conversationId, setReturnedCaption]);

  // Edit mode loads the message text and puts back whatever draft was there
  // before when it ends. Replying keeps the draft as is.
  const editingId = editing?.id ?? null;
  const restoredReplyIdRef = useRef<string | null>(null);
  // A draft's reply target not yet checked (or not checkable offline): kept
  // in the draft, but no bar until the message itself is in hand (D228).
  const pendingReplyIdRef = useRef<string | null>(
    initialDraft?.replyToId ?? null
  );
  const draftBeforeEditRef = useRef('');
  const previousEditingIdRef = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousEditingIdRef.current;
    previousEditingIdRef.current = editingId;
    if (editingId && editingId !== previous && editing) {
      if (!previous) draftBeforeEditRef.current = messageInput;
      setMessageInput(editing.content);
    } else if (!editingId && previous) {
      setMessageInput(draftBeforeEditRef.current);
      draftBeforeEditRef.current = '';
    }
    // A reply put back from a draft doesn't pop the keyboard (D228); any
    // other reply or edit here replaces the one still being checked.
    const restored =
      mode?.kind === 'reply' && mode.message.id === restoredReplyIdRef.current;
    restoredReplyIdRef.current = null;
    if (mode && mode.message.conversationId === conversationId) {
      pendingReplyIdRef.current = null;
    }
    if (mode && !restored) textareaRef.current?.focus();
    // Only when the mode or its message changes, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, mode?.kind, mode?.message.id]);

  // Drafts (D228): kept on this device as you type and when you leave the
  // chat. While editing, the draft is what was there before the edit.
  const setDrafts = useSetAtom(draftsAtom);
  const draftNowRef = useRef({ text: '', replyToId: null as string | null });
  draftNowRef.current = {
    text: editing ? draftBeforeEditRef.current : messageInput,
    // Only this chat's reply (a switch passes the old chat's mode for one
    // render).
    replyToId:
      replyingTo && replyingTo.conversationId === conversationId
        ? replyingTo.id
        : null,
  };
  // Who is signed in now, not at render: signing out unmounts the composer
  // after the drafts were forgotten, and must not write this one back.
  const store = useStore();
  const ownerId = currentUser?.id ?? null;
  const persistDraft = useCallback(() => {
    if (!ownerId || store.get(currentUserAtom)?.id !== ownerId) return;
    const { text } = draftNowRef.current;
    const replyToId =
      draftNowRef.current.replyToId ?? pendingReplyIdRef.current;
    setDrafts(
      saveDraft(ownerId, conversationId, { text, replyToId, familyId })
    );
  }, [ownerId, store, conversationId, familyId, setDrafts]);

  // The draft's reply target, looked up as it is now: deleted or gone means
  // the reply is dropped, never shown from memory (D228).
  const loadedRef = useRef(loadedMessages ?? []);
  loadedRef.current = loadedMessages ?? [];
  const onRestoreReplyRef = useRef(onRestoreReply);
  onRestoreReplyRef.current = onRestoreReply;
  useEffect(() => {
    const replyToId = pendingReplyIdRef.current;
    if (!replyToId) return;
    let cancelled = false;
    void resolveDraftReply(
      replyToId,
      conversationId,
      loadedRef.current,
      async (id) =>
        (await api.getMessage(id)).data.message as unknown as ChatMessage
    ).then((target) => {
      if (cancelled || pendingReplyIdRef.current !== replyToId) return;
      if (target === 'unknown') return;
      pendingReplyIdRef.current = null;
      if (target) {
        restoredReplyIdRef.current = target.id;
        onRestoreReplyRef.current?.(target);
      } else {
        persistDraft();
      }
    });
    return () => {
      cancelled = true;
    };
    // Once per chat: the composer is keyed by chat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const timer = setTimeout(persistDraft, DRAFT_SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [messageInput, replyingTo?.id, editingId, persistDraft]);
  useEffect(() => () => persistDraft(), [persistDraft]);

  // Typing indicator (D24): announce at most every few seconds while typing,
  // and stop as soon as the box is cleared or the message sent.
  const lastTypingSentRef = useRef(0);
  const announceTyping = (typing: boolean) => {
    const now = Date.now();
    if (typing) {
      if (
        now - lastTypingSentRef.current <
        MESSAGE_LIMITS.TYPING_SEND_INTERVAL_MS
      ) {
        return;
      }
      lastTypingSentRef.current = now;
    } else {
      if (lastTypingSentRef.current === 0) return;
      lastTypingSentRef.current = 0;
    }
    sendTyping(conversationId, typing);
  };

  // Grow the textarea with its content.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    // Empty: one line. Chrome counts a wrapped placeholder in scrollHeight,
    // which made the empty pill two lines tall on a phone (M69 review).
    if (messageInput === '') return;
    textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_INPUT_HEIGHT_PX)}px`;
  }, [messageInput]);

  // Uploads run in lib/attachments.ts and show as pending bubbles in the
  // message list, so the composer is free again immediately.
  const send = (
    request: Omit<AttachmentRequest, 'conversationId' | 'onSent'>
  ) => sendAttachment({ ...request, conversationId, onSent });

  // Photos resized (D135) and videos with a poster, as Share does (D181).
  const handleImageFile = async (file: File) => {
    await sendFile(conversationId, file, { photoQuality, onSent, as: 'image' });
  };

  const handleVideoFile = async (file: File) => {
    await sendFile(conversationId, file, { photoQuality, onSent, as: 'video' });
  };

  // The card is drawn at 1080×1080 already, so it skips the photo resize.
  const sendGreeting = async (card: CardChoice, line: string) => {
    setGreeting({ open: false, occasion: null });
    try {
      const file = await renderCard(card, line, currentUser?.displayName ?? '');
      const { width, height, thumbnail } = await prepareImage(file);
      send({
        kind: 'image',
        file,
        fileName: file.name,
        meta: { width, height },
        thumbnail,
        previewUrl: URL.createObjectURL(thumbnail ?? file),
      });
    } catch (err) {
      toast({
        title: t('chat.composer.cardFailed'),
        description: errorMessage(err),
        status: 'error',
      });
    }
  };

  const handleAnyFile = async (file: File) => {
    send({ kind: 'file', file, fileName: file.name });
  };

  // Peaks decoded from the recording travel sealed with it (D221); the
  // levels captured while recording are the fallback.
  const sendVoiceNote = async (voice: VoiceRecording) => {
    const waveform = (await computePeaks(voice.blob)) ?? voice.waveform;
    send({
      kind: 'voice',
      file: voice.blob,
      fileName: voice.fileName,
      meta: { duration: voice.duration },
      extraMetadata: { waveform },
    });
  };

  // Hitting the length limit sends what was recorded rather than losing it.
  const recorder = useVoiceRecorder((voice) => void sendVoiceNote(voice));

  // A "+" action that takes over the screen (pickers, sheets) lets the
  // keyboard go; opening "+" itself never does (D272).
  const letKeyboardGo = () => textareaRef.current?.blur();

  const startRecording = async () => {
    // Recording replaces the text field: the keyboard goes down (D272).
    letKeyboardGo();
    try {
      await recorder.start();
    } catch (err) {
      toast({
        title: t('chat.composer.micUnavailable'),
        description:
          err instanceof Error && err.message.includes('not supported')
            ? err.message
            : t('chat.composer.allowMic'),
        status: 'error',
      });
    }
  };

  const finishRecording = async () => {
    const voice = await recorder.finish();
    if (voice) await sendVoiceNote(voice);
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const item = Array.from(event.clipboardData.items).find((entry) =>
      entry.type.startsWith('image/')
    );
    const file = item?.getAsFile();
    if (file) {
      event.preventDefault();
      void handleImageFile(file);
    }
  };

  const sendAlbumDraft = () => {
    const files = albumDraft.map((item) => item.file);
    const caption = messageInput.trim();
    dropDraft();
    setMessageInput('');
    announceTyping(false);
    void sendAlbums(conversationId, files, {
      photoQuality,
      onSent,
      caption: caption || undefined,
      replyToId: replyingTo?.id,
    }).catch((err) =>
      toast({
        title: t('chat.send.couldNotSend'),
        description: errorMessage(err),
        status: 'error',
      })
    );
    if (replyingTo) onCancelMode();
  };

  const handleSendMessage = async () => {
    if (albumDraft.length > 0 && !editing) {
      sendAlbumDraft();
      return;
    }
    if (!messageInput.trim()) return;
    const content = messageInput.trim();

    if (editing) {
      if (content === editing.content) {
        onCancelMode();
        return;
      }
      try {
        // An edit is sealed again when this device has its key (M28).
        const response =
          isE2EEReady() && editing.clientId
            ? await editSealed({
                messageId: editing.id,
                conversationId,
                clientId: editing.clientId,
                text: content,
                ...declaredMentions(content),
              })
            : await api.editMessage(editing.id, content);
        onEdited(response.data as ChatMessage);
        onCancelMode();
      } catch (err) {
        // Offline: show the edit and send it when we're back (M29, D69).
        if (isRetryable(err)) {
          queueChange({
            kind: 'edit',
            conversationId,
            messageId: editing.id,
            messageClientId: editing.clientId ?? undefined,
            content,
            ...declaredMentions(content),
          });
          onEdited({
            ...editing,
            content,
            editedAt: new Date().toISOString(),
          });
          onCancelMode();
          return;
        }
        toast({
          title: t('chat.composer.editFailed'),
          description: errorMessage(err),
          status: 'error',
        });
      }
      return;
    }

    setMessageInput('');
    setMentionQuery(null);
    announceTyping(false);
    // Shows at once and goes out in the background (M23, D31); the outbox
    // retries it if the connection drops.
    queueMessage({
      conversationId,
      content,
      ...declaredMentions(content),
      replyToId: replyingTo?.id,
      replyTo: replyingTo
        ? {
            id: replyingTo.id,
            senderId: replyingTo.sender?.id ?? null,
            senderName: replyingTo.sender?.displayName ?? t('chat.unknown'),
            type: replyingTo.type,
            preview: replyLabel(replyingTo),
            deleted: false,
            createdAt: replyingTo.createdAt,
          }
        : null,
    });
    if (replyingTo) onCancelMode();
  };

  // "+" → Send later…: text only (D239), sealed here now, sent at its time.
  const openSendLater = () => {
    if (albumDraft.length > 0 || editing) {
      toast({ title: t('chat.sendLater.textOnly'), status: 'info' });
      return;
    }
    if (!messageInput.trim()) {
      toast({ title: t('chat.sendLater.needText'), status: 'info' });
      textareaRef.current?.focus();
      return;
    }
    if (!isE2EEReady()) {
      toast({ title: t('chat.sendLater.needsKeys'), status: 'info' });
      return;
    }
    letKeyboardGo();
    setSendLaterOpen(true);
  };
  const scheduleNow = async (at: Date) => {
    const content = messageInput.trim();
    if (!content) return;
    try {
      const response = await scheduleText({
        conversationId,
        text: content,
        scheduledFor: at,
        ...declaredMentions(content),
        replyToId: replyingTo?.id,
      });
      scheduled.add(response.data as ScheduledView);
      setMessageInput('');
      setMentionQuery(null);
      announceTyping(false);
      if (replyingTo) onCancelMode();
      toast({
        title: t('chat.sendLater.done', {
          time: scheduledLabel(response.data.scheduledFor),
        }),
        status: 'success',
        duration: 2500,
      });
    } catch (err) {
      toast({
        title:
          err instanceof KeysNotReadyError
            ? t('chat.sendLater.needsKeys')
            : t('chat.sendLater.failed'),
        description:
          err instanceof KeysNotReadyError
            ? undefined
            : (scheduleErrorText(err) ?? errorMessage(err)),
        status: 'error',
      });
    }
  };
  const sendScheduledNow = async (item: ScheduledView) => {
    scheduled.drop(item.id);
    const attempt = () => api.sendScheduledNow(conversationId, item.id);
    try {
      let response;
      try {
        response = await attempt();
      } catch (err) {
        // The chat moved to a newer key: seal it again here, then send.
        if (
          err instanceof ApiError &&
          (err.code === 'STALE_KEY' || err.code === 'ROTATION_REQUIRED') &&
          // The same attempt the app-wide reseal makes, not a second one.
          (await resealOnce({ ...item, needsReseal: true }))
        ) {
          response = await attempt().catch((again) => {
            // Sealing again past its time sent it already, or it's on its
            // way: either way it's sent.
            if (
              again instanceof ApiError &&
              (again.code === 'SCHEDULED_NOT_FOUND' ||
                again.code === 'SCHEDULED_SENDING')
            )
              return null;
            throw again;
          });
        } else {
          throw err;
        }
      }
      const message = response?.data.message as SentMessage | null | undefined;
      if (message) onSent(message);
    } catch (err) {
      void scheduled.reload();
      toast({
        title: t('chat.scheduled.failed'),
        description: errorMessage(err),
        status: 'error',
      });
    }
  };
  const cancelScheduled = async (item: ScheduledView) => {
    scheduled.drop(item.id);
    try {
      await api.cancelScheduled(conversationId, item.id);
      toast({
        title: t('chat.scheduled.cancelled'),
        status: 'success',
        duration: 2000,
      });
    } catch (err) {
      void scheduled.reload();
      toast({
        title: t('chat.scheduled.failed'),
        description: errorMessage(err),
        status: 'error',
      });
    }
  };

  // Who a message @mentions, by id: the server can't read encrypted text, so
  // the sender says (M28, D61).
  function declaredMentions(text: string) {
    const handles = extractMentionHandles(text);
    return {
      mentions: mentionCandidates
        .filter(
          (candidate) =>
            candidate.id !== EVERYONE_MENTION &&
            handles.includes(candidate.handle)
        )
        .map((candidate) => candidate.id),
      mentionsEveryone:
        handles.includes(EVERYONE_MENTION) &&
        mentionCandidates.some(
          (candidate) => candidate.id === EVERYONE_MENTION
        ),
    };
  }

  const mentionMatches =
    mentionQuery === null
      ? []
      : filterMentionCandidates(mentionCandidates, mentionQuery);

  const updateMentionQuery = (textarea: HTMLTextAreaElement) => {
    const query = mentionQueryAt(textarea.value, textarea.selectionStart);
    setMentionQuery(query);
    if (query !== mentionQuery) setMentionIndex(0);
  };

  const pickMention = (candidate: MentionCandidate) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const next = insertMention(
      textarea.value,
      textarea.selectionStart,
      candidate.handle
    );
    setMessageInput(next.text);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(next.caret, next.caret);
    });
  };

  const openEmoji = () => {
    const textarea = textareaRef.current;
    if (desktop) {
      setEmojiOpen(true);
      return;
    }
    // Typing = the text field has focus and the keyboard is up. Android's
    // back button lowers the keyboard but keeps the focus, so both count.
    // On Android (resizes-content) innerHeight shrinks with the keyboard, so
    // lib/viewport measures it against the tallest view seen (M69 review).
    const typing =
      document.activeElement === textarea &&
      keyboardHeight() >= KEYBOARD_MIN_PX;
    setEmojiPanelHeight(emojiPanelHeightNow());
    closeEmojiAfterPick.current = !typing;
    setEmojiSearching(false);
    // The keyboard goes down; the panel takes its place.
    textarea?.blur();
    setEmojiOpen(true);
  };

  const closeEmoji = (refocus: boolean) => {
    setEmojiOpen(false);
    if (refocus) requestAnimationFrame(() => textareaRef.current?.focus());
  };

  // At the caret, replacing any selection, caret after it (D263). The
  // textarea keeps its selection while the picker has focus.
  const insertEmoji = (emoji: string) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? messageInput.length;
    const end = textarea?.selectionEnd ?? start;
    const next = insertAtCaret(messageInput, start, end, emoji);
    setMessageInput(next.text);
    if (!editing) announceTyping(true);
    requestAnimationFrame(() =>
      textareaRef.current?.setSelectionRange(next.caret, next.caret)
    );
    if (!desktop && closeEmojiAfterPick.current) setEmojiOpen(false);
  };

  // Desktop: a click outside the popover closes it (the button toggles).
  useEffect(() => {
    if (!emojiOpen || !desktop) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        emojiPopoverRef.current?.contains(target) ||
        emojiButtonRef.current?.contains(target)
      ) {
        return;
      }
      setEmojiOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [emojiOpen, desktop]);

  const emojiButton = (
    <IconButton
      aria-label={t(
        emojiOpen && !desktop ? 'chat.emoji.keyboard' : 'chat.emoji.open'
      )}
      aria-expanded={emojiOpen}
      aria-haspopup="dialog"
      data-testid="emoji-button"
      ref={emojiButtonRef}
      icon={
        emojiOpen && !desktop ? <Keyboard size={22} /> : <Smile size={22} />
      }
      variant="ghost"
      colorScheme="gray"
      color="muted"
      flexShrink={0}
      // Keep the phone keyboard up until we decide (the panel replaces it).
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        if (!emojiOpen) openEmoji();
        else if (desktop) closeEmoji(true);
        else {
          // iPhone raises the keyboard only for a focus inside the tap
          // itself, not one in a later frame.
          textareaRef.current?.focus();
          setEmojiOpen(false);
        }
      }}
    />
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mentionMatches.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setMentionIndex(
          (index) =>
            (index + step + mentionMatches.length) % mentionMatches.length
        );
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
        e.preventDefault();
        pickMention(
          mentionMatches[Math.min(mentionIndex, mentionMatches.length - 1)]!
        );
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionQuery(null);
        return;
      }
    }
    if (e.key === 'Escape' && mode) {
      e.preventDefault();
      onCancelMode();
      return;
    }
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !e.nativeEvent.isComposing &&
      enterSends()
    ) {
      e.preventDefault();
      void handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    typedRef.current = true;
    setMessageInput(e.target.value);
    updateMentionQuery(e.target);
    if (!editing) announceTyping(e.target.value.length > 0);
  };

  // Two or more photos or videos make an album (D219), shown in a strip to
  // check before sending; one goes straight away, as before. Albums are
  // sealed, so a device still getting its keys sends them one by one.
  const pickPhotos = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (isE2EEReady() && (files.length > 1 || albumDraft.length > 0)) {
      setAlbumDraft((draft) => [
        ...draft,
        ...files.map((file) => ({
          file,
          url: shareKind(file) === 'video' ? null : URL.createObjectURL(file),
        })),
      ]);
      textareaRef.current?.focus();
      return;
    }
    void (async () => {
      for (const file of files.slice(0, MAX_PICKED_FILES)) {
        await (shareKind(file) === 'video'
          ? handleVideoFile(file)
          : handleImageFile(file));
      }
    })();
  };

  const pickFile =
    (handler: (file: File) => Promise<void>) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Several photos or files at once go up together (Living Room
      // "Sending… 2 of 3 photos"), in the order picked.
      const files = Array.from(event.target.files ?? []).slice(
        0,
        MAX_PICKED_FILES
      );
      event.target.value = '';
      void (async () => {
        for (const file of files) await handler(file);
      })();
    };

  return (
    // A floating pill over the timeline (Living Room), not a docked bar.
    <Box
      position="relative"
      px={3}
      pt={1}
      pb="max(env(safe-area-inset-bottom), 12px)"
      flexShrink={0}
    >
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        hidden
        data-testid="photo-input"
        onChange={pickPhotos}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        hidden
        onChange={pickFile(handleVideoFile)}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.txt"
        multiple
        hidden
        onChange={pickFile(handleAnyFile)}
      />

      <PollSheet
        isOpen={pollOpen}
        onClose={() => setPollOpen(false)}
        onSend={(question, poll) => {
          setPollOpen(false);
          // Through the outbox like text: sealed, kept, retried (D66-D68).
          queueMessage({
            conversationId,
            content: question,
            poll,
            replyToId: replyingTo?.id,
          });
          if (replyingTo) onCancelMode();
        }}
      />
      <GatheringSheet
        isOpen={gatheringOpen}
        onClose={() => setGatheringOpen(false)}
        // Everyone in the chat, you too; @everyone isn't a person.
        participantCount={
          mentionCandidates.filter((c) => c.id !== EVERYONE_MENTION).length + 1
        }
        onSend={(title, gathering) => {
          setGatheringOpen(false);
          // Through the outbox like a poll: sealed, kept, retried (D66-D68).
          queueMessage({
            conversationId,
            content: title,
            gathering,
            replyToId: replyingTo?.id,
          });
          if (replyingTo) onCancelMode();
        }}
      />
      <SendLaterSheet
        isOpen={sendLaterOpen}
        onClose={() => setSendLaterOpen(false)}
        onSchedule={(at) => void scheduleNow(at)}
      />
      {!recorder.recording && (
        <ScheduledList
          items={scheduled.items}
          // Only once messages are on screen, never beside the page mark.
          loading={scheduled.loading && (loadedMessages?.length ?? 0) > 0}
          onSendNow={(item) => void sendScheduledNow(item)}
          onCancel={(item) => void cancelScheduled(item)}
        />
      )}
      <GreetingSheet
        isOpen={greeting.open}
        onClose={() => setGreeting({ open: false, occasion: null })}
        initialOccasion={greeting.occasion}
        initialDay={greeting.day}
        recipientName={recipientName}
        senderName={currentUser?.displayName ?? ''}
        onSend={(card, line) => void sendGreeting(card, line)}
      />

      {mode && !recorder.recording && (
        <HStack
          mb={2}
          px={3}
          py={2}
          bg="surface"
          borderWidth="1px"
          borderColor="line"
          borderLeftWidth="3px"
          borderLeftColor="accent"
          borderRadius="18px"
          spacing={2}
        >
          <Box color="accent" flexShrink={0}>
            {mode.kind === 'reply' ? <Reply size={16} /> : <Pencil size={16} />}
          </Box>
          <Box flex="1" minW={0}>
            <Text fontSize="13px" fontWeight={700} color="accent">
              {mode.kind === 'reply'
                ? mode.message.sender?.displayName
                  ? t('chat.composer.replyingTo', {
                      name: mode.message.sender.displayName,
                    })
                  : t('chat.composer.replyingToMessage')
                : t('chat.composer.editing')}
            </Text>
            <Text fontSize="15px" noOfLines={1}>
              {shortenLinks(replyLabel(mode.message))}
            </Text>
          </Box>
          <IconButton
            aria-label={t(
              mode.kind === 'reply'
                ? 'chat.composer.cancelReply'
                : 'chat.composer.cancelEdit'
            )}
            icon={<X size={16} />}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            onClick={onCancelMode}
          />
        </HStack>
      )}

      {albumDraft.length > 0 && !recorder.recording && (
        <Box
          mb={2}
          p={2}
          bg="surface"
          borderWidth="1px"
          borderColor="line"
          borderRadius="18px"
          data-testid="album-strip"
        >
          <HStack justify="space-between" px={1} pb={2}>
            <Text fontSize="13px" fontWeight={700} color="accent">
              {t('chat.album.count', { n: albumDraft.length })}
            </Text>
            <IconButton
              aria-label={t('chat.album.clear')}
              icon={<X size={16} />}
              size="sm"
              variant="ghost"
              colorScheme="gray"
              onClick={() => dropDraft()}
            />
          </HStack>
          <HStack spacing="6px" overflowX="auto" pb={1}>
            {albumDraft.map((item, index) => (
              <Box
                key={`${item.file.name}-${index}`}
                position="relative"
                w="64px"
                h="64px"
                flexShrink={0}
                borderRadius="12px"
                overflow="hidden"
                bg="surface.subtle"
              >
                {item.url ? (
                  <Image
                    src={item.url}
                    alt={item.file.name}
                    w="full"
                    h="full"
                    objectFit="cover"
                  />
                ) : (
                  <Center h="full" color="muted">
                    <Video size={22} aria-label={item.file.name} />
                  </Center>
                )}
                <IconButton
                  aria-label={t('chat.album.remove', { name: item.file.name })}
                  icon={<X size={12} />}
                  position="absolute"
                  top="2px"
                  right="2px"
                  size="xs"
                  minW="22px"
                  h="22px"
                  borderRadius="full"
                  colorScheme="gray"
                  bg="rgba(31, 29, 27, 0.7)"
                  color="white"
                  _hover={{ bg: 'rgba(31, 29, 27, 0.9)' }}
                  onClick={() => dropDraft((i) => i !== index)}
                />
              </Box>
            ))}
          </HStack>
        </Box>
      )}

      {emojiOpen && desktop && !recorder.recording && (
        // Above the pill, from its start edge, like the @mention picker.
        <Box
          ref={emojiPopoverRef}
          position="absolute"
          bottom="100%"
          insetStart={3}
          mb={2}
          w="360px"
          maxW="calc(100% - 24px)"
          h="420px"
          maxH="calc(var(--app-height, 100vh) - 160px)"
          zIndex="popover"
          bg="surface"
          borderRadius="card"
          overflow="hidden"
          boxShadow="0 0 0 1px var(--chakra-colors-line), 0 16px 32px -12px rgba(31, 29, 27, 0.35)"
          data-testid="emoji-popover"
        >
          <LazyEmojiPicker
            mode="insert"
            autoFocusSearch
            onPick={insertEmoji}
            onClose={() => closeEmoji(true)}
          />
        </Box>
      )}

      {!recorder.recording && (
        <MentionPicker
          candidates={mentionMatches}
          familyId={familyId}
          activeIndex={Math.min(
            mentionIndex,
            Math.max(0, mentionMatches.length - 1)
          )}
          onPick={pickMention}
        />
      )}

      <Box
        // The "+" menu is placed above this pill (D272).
        position="relative"
        bg="surface"
        borderRadius={multiline ? '24px' : '30px'}
        p="6px"
        boxShadow="0 0 0 1px var(--chakra-colors-line), 0 12px 24px -10px rgba(31, 29, 27, 0.3)"
      >
        {recorder.recording ? (
          // Recording bar: discard, elapsed time, live level, send.
          <HStack spacing={2} h="44px">
            <IconButton
              aria-label={t('chat.composer.discardVoice')}
              icon={<Trash2 size={18} />}
              variant="ghost"
              colorScheme="red"
              color="danger"
              onClick={recorder.cancel}
            />
            <Box
              w="10px"
              h="10px"
              borderRadius="full"
              bg="danger"
              flexShrink={0}
              sx={{
                animation: 'twinden-pulse 1.2s ease-in-out infinite',
                '@keyframes twinden-pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                },
              }}
            />
            <Text
              fontSize="sm"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
              flexShrink={0}
              minW="38px"
            >
              {formatTime(recorder.seconds)}
            </Text>
            <Waveform
              // Most recent levels, right-aligned so the meter scrolls left.
              bars={[
                ...Array(Math.max(0, 40 - recorder.liveLevels.length)).fill(0),
                ...recorder.liveLevels.map((level) =>
                  Math.min(100, Math.round(Math.sqrt(level) * 180))
                ),
              ]}
              color={waveColor}
              height={28}
            />
            <IconButton
              aria-label={t('chat.composer.sendVoice')}
              icon={<Send size={18} />}
              colorScheme="brand"
              onClick={() => void finishRecording()}
            />
          </HStack>
        ) : (
          <HStack spacing="6px" align="flex-end">
            <ComposerPlusMenu
              label={t('chat.composer.attach')}
              items={[
                {
                  key: 'photos',
                  icon: <ImageIcon size={16} />,
                  label: t('chat.composer.photosVideos'),
                  onSelect: () => {
                    letKeyboardGo();
                    imageInputRef.current?.click();
                  },
                },
                {
                  key: 'video',
                  icon: <Video size={16} />,
                  label: t('chat.type.video'),
                  onSelect: () => {
                    letKeyboardGo();
                    videoInputRef.current?.click();
                  },
                },
                {
                  key: 'file',
                  icon: <FileText size={16} />,
                  label: t('chat.type.file'),
                  onSelect: () => {
                    letKeyboardGo();
                    fileInputRef.current?.click();
                  },
                },
                {
                  key: 'poll',
                  icon: <BarChart3 size={16} />,
                  label: t('chat.composer.poll'),
                  onSelect: () => {
                    // Polls are sealed (D220): not until this device has
                    // its keys. A toast doesn't take over the screen.
                    if (!isE2EEReady()) {
                      toast({ title: t('chat.poll.needsKeys') });
                      return;
                    }
                    letKeyboardGo();
                    setPollOpen(true);
                  },
                },
                {
                  key: 'gathering',
                  icon: <CalendarClock size={16} />,
                  label: t('chat.composer.gathering'),
                  onSelect: () => {
                    // Sealed like polls (D282): not until this device has
                    // its keys.
                    if (!isE2EEReady()) {
                      toast({ title: t('chat.gathering.needsKeys') });
                      return;
                    }
                    letKeyboardGo();
                    setGatheringOpen(true);
                  },
                },
                {
                  key: 'greeting',
                  icon: <Gift size={16} />,
                  label: t('chat.greeting.title'),
                  onSelect: () => {
                    letKeyboardGo();
                    setGreeting({ open: true, occasion: null });
                  },
                },
                {
                  key: 'sendLater',
                  icon: <Clock size={16} />,
                  label: t('chat.composer.sendLater'),
                  onSelect: openSendLater,
                },
              ]}
            />

            {emojiButton}

            <Textarea
              ref={textareaRef}
              placeholder={
                albumDraft.length > 0
                  ? t('chat.album.captionPlaceholder')
                  : placeholder
              }
              aria-label={
                albumDraft.length > 0
                  ? t('chat.album.captionPlaceholder')
                  : placeholder
              }
              value={messageInput}
              onChange={handleInputChange}
              onSelect={(event) => updateMentionQuery(event.currentTarget)}
              onBlur={() => setMentionQuery(null)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onFocus={() => {
                // Back to typing: the keyboard replaces the emoji panel.
                if (emojiOpen && !desktop) setEmojiOpen(false);
                onFocus();
              }}
              rows={1}
              resize="none"
              variant="unstyled"
              fontSize="17px"
              lineHeight="24px"
              minH="44px"
              px="6px"
              py="10px"
              overflowY="auto"
              enterKeyHint={enterSends() ? 'send' : 'enter'}
              // The placeholder stays on one line, cut with "…" on a narrow
              // phone or in a longer language.
              sx={{
                '&::placeholder': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />

            {/* One slot: send when there is text, record when there isn't. */}
            {messageInput.trim() || editing || albumDraft.length > 0 ? (
              <IconButton
                aria-label={t(
                  editing
                    ? 'chat.composer.saveEdit'
                    : albumDraft.length > 0
                      ? 'chat.album.send'
                      : 'chat.composer.send'
                )}
                icon={editing ? <Check size={20} /> : <Send size={20} />}
                colorScheme="brand"
                flexShrink={0}
                // Keep focus (and the phone keyboard) in the textarea on tap.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void handleSendMessage()}
              />
            ) : (
              <IconButton
                aria-label={t('chat.composer.record')}
                icon={<Mic size={22} />}
                colorScheme="brand"
                flexShrink={0}
                onClick={() => void startRecording()}
              />
            )}
          </HStack>
        )}
      </Box>

      {emojiOpen && !desktop && !recorder.recording && (
        <Box
          mt={2}
          h={
            emojiSearching
              ? `min(${emojiPanelHeight}px, max(220px, calc(var(--app-height, 100vh) * 0.45)))`
              : `${emojiPanelHeight}px`
          }
          data-searching={emojiSearching || undefined}
          bg="surface"
          borderRadius="card"
          boxShadow="0 0 0 1px var(--chakra-colors-line)"
          overflow="hidden"
          data-testid="emoji-panel"
        >
          <LazyEmojiPicker
            mode="insert"
            onPick={insertEmoji}
            onClose={() => closeEmoji(true)}
            onSearchFocusChange={setEmojiSearching}
          />
        </Box>
      )}
    </Box>
  );
}
